import { clientEnv } from "../env";

export type UltraSeatType = "member" | "creator" | "builder" | "admin";
export type UltraFounderRole = "creator" | "creator_partner";

export interface UltraOrganization {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly plan: "ultra";
  readonly status: "active" | "suspended";
  readonly monthlyCreditLimit: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UltraMember {
  readonly organizationId: string;
  readonly userId: string;
  readonly seat: UltraSeatType;
  readonly founderRole?: UltraFounderRole;
  readonly displayName?: string;
  readonly email?: string;
  readonly status: "active" | "disabled";
  readonly monthlyCreditLimit?: number;
  readonly joinedAt: string;
  readonly updatedAt: string;
}

export interface UltraWorkspaceSummary {
  readonly organization: UltraOrganization;
  readonly membership: UltraMember;
  readonly permissions: readonly string[];
}

export interface UltraWorkspace extends UltraWorkspaceSummary {
  readonly members: readonly UltraMember[];
}

export interface UltraEntitlements {
  readonly plan: "ultra";
  readonly seats: readonly {
    readonly id: UltraSeatType;
    readonly name: string;
    readonly description: string;
    readonly permissions: readonly string[];
  }[];
}

export interface UltraUsageSummary {
  readonly organizationId: string;
  readonly period: string;
  readonly monthlyCreditLimit: number;
  readonly creditsUsed: number;
  readonly creditsRemaining: number;
  readonly perMember: readonly {
    readonly userId: string;
    readonly seat: UltraSeatType;
    readonly status: string;
    readonly monthlyCreditLimit: number | null;
    readonly creditsUsed: number;
    readonly requestCount: number;
  }[];
}

const ACTIVE_ORGANIZATION_KEY = "omnisynia.ultra.active-organization.v1";

function authHeaders(token: string): HeadersInit {
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

async function readJson<T>(response: Response): Promise<T> {
  const payload = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? "No se pudo acceder a OMNISYNIA Ultra.");
  return payload;
}

export function getActiveUltraOrganizationId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_ORGANIZATION_KEY);
  } catch {
    return null;
  }
}

export function setActiveUltraOrganizationId(organizationId: string): void {
  try {
    window.localStorage.setItem(ACTIVE_ORGANIZATION_KEY, organizationId);
  } catch {
    // La sesión sigue funcionando aunque el navegador bloquee el almacenamiento local.
  }
}

export function clearActiveUltraOrganizationId(): void {
  try {
    window.localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  } catch {
    // El cambio de cuenta sigue funcionando aunque el navegador bloquee el almacenamiento local.
  }
}

export async function getUltraEntitlements(): Promise<UltraEntitlements> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/entitlements`);
  return readJson<UltraEntitlements>(response);
}

export async function listUltraOrganizations(token: string): Promise<UltraWorkspaceSummary[]> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/organizations`, { headers: authHeaders(token) });
  return (await readJson<{ organizations: UltraWorkspaceSummary[] }>(response)).organizations;
}

export async function getUltraWorkspace(token: string, organizationId: string): Promise<UltraWorkspace> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/organizations/${organizationId}`, {
    headers: authHeaders(token)
  });
  return readJson<UltraWorkspace>(response);
}

export async function createUltraOrganization(
  token: string,
  input: { name: string; monthlyCreditLimit: number }
): Promise<UltraWorkspace> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/organizations`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(input)
  });
  return readJson<UltraWorkspace>(response);
}

export async function getUltraUsage(token: string, organizationId: string): Promise<UltraUsageSummary> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/organizations/${organizationId}/usage`, {
    headers: authHeaders(token)
  });
  return readJson<UltraUsageSummary>(response);
}

export async function setUltraBudget(
  token: string,
  organizationId: string,
  monthlyCreditLimit: number
): Promise<UltraOrganization> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/organizations/${organizationId}/budget`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({ monthlyCreditLimit })
  });
  return readJson<UltraOrganization>(response);
}

export async function addUltraMember(
  token: string,
  organizationId: string,
  input: { email: string; seat: UltraSeatType; founderRole?: UltraFounderRole; monthlyCreditLimit?: number }
): Promise<UltraMember> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ultra/organizations/${organizationId}/members`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(input)
  });
  return readJson<UltraMember>(response);
}
